import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { FileSignature, Printer, X } from "lucide-react";

// === Helpers === //
const formatDateRO = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const todayPlusMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// === Sport → Federație mapping === //
const isBaschet = (sport) => {
  if (!sport) return false;
  const s = String(sport).toLowerCase().trim();
  return s === "baschet" || s === "basket" || s === "basketball" || s === "minibaschet";
};

const isVolei = (sport) => {
  if (!sport) return false;
  const s = String(sport).toLowerCase().trim();
  return s === "volei" || s === "volley" || s === "volleyball" || s === "minivolei";
};

// Returnează "baschet" | "volei" | null
const detectFederatie = (sport) => {
  if (isBaschet(sport)) return "baschet";
  if (isVolei(sport)) return "volei";
  return null;
};

// Funcție exportată pentru a verifica dacă pacientul are sport eligibil pentru adeverință
export const hasAdeverinta = (sport) => detectFederatie(sport) !== null;
// Backwards compat (Baschet)
export { isBaschet };

// === Componentă principală === //
export const AdeverintaButton = ({ patient }) => {
  const [open, setOpen] = useState(false);
  const dp = patient?.date_personale || {};
  const a1 = patient?.atelier_1 || {};
  const is = patient?.info_sportive || {};
  const federatie = detectFederatie(is.sport_practicat);

  // Talia: atelier_1.talie e în metri (ex 1.78), convertim la cm
  const taliaCm = a1.talie ? Math.round(parseFloat(a1.talie) * 100) : "";

  const [form, setForm] = useState(() => ({
    nume: dp.nume || "",
    prenume: dp.prenume || "",
    data_nasterii: dp.data_nasterii || "",
    loc_nasterii: dp.localitate || "",
    tara: "România",
    cnp: dp.cnp || "",
    talia: taliaCm ? String(taliaCm) : "",
    data_examen: todayISO(),
    viza_de_la: todayISO(),
    viza_pana_la: todayPlusMonths(6),
    medic_nume: "",
    medic_specialitate: "Medic Specialist Medicină Sportivă",
    medic_cod: "",
    functie_responsabil: "",
    nume_responsabil: "",
  }));

  // Re-init la deschidere
  useMemo(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        nume: dp.nume || "",
        prenume: dp.prenume || "",
        data_nasterii: dp.data_nasterii || "",
        loc_nasterii: dp.localitate || "",
        cnp: dp.cnp || "",
        talia: taliaCm ? String(taliaCm) : "",
      }));
    }
  }, [open, dp.nume, dp.prenume, dp.data_nasterii, dp.localitate, dp.cnp, taliaCm]);

  const handle = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // CNP rendered ca 13 căsuțe individuale
  const cnpDigits = (form.cnp || "").replace(/\D/g, "").padEnd(13, " ").slice(0, 13).split("");

  if (!federatie) return null;

  const handlePrint = () => window.print();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 w-full justify-start text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
          data-testid="adeverinta-btn"
        >
          <FileSignature className="w-4 h-4" />
          Generează adeverință medicală
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible print:p-0 print:shadow-none print:border-0">
        <DialogHeader className="print:hidden">
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-primary" />
            Adeverință medicală — {federatie === "baschet" ? "Federația Română de Baschet" : "Federația Română de Volei"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Datele sunt pre-completate automat din formular și Atelier 1. Editează manual câmpurile rămase, apoi tipărește.
          </p>
        </DialogHeader>

        {/* Form de editare (doar pe ecran) */}
        <div className="print:hidden grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border" data-testid="adeverinta-form">
          {federatie === "volei" && (
            <FormRow label="Loc nașterii (editabil)" id="f-locn">
              <Input id="f-locn" value={form.loc_nasterii} onChange={(e) => handle("loc_nasterii", e.target.value)} data-testid="adev-loc-nasterii" />
            </FormRow>
          )}
          <FormRow label="Talie / Înălțime (cm)" id="f-talia">
            <Input id="f-talia" value={form.talia} onChange={(e) => handle("talia", e.target.value)} data-testid="adev-talia" />
          </FormRow>
          {federatie === "volei" ? (
            <FormRow label="Data examen medical" id="f-exam">
              <Input id="f-exam" type="date" value={form.data_examen} onChange={(e) => handle("data_examen", e.target.value)} data-testid="adev-data-examen" />
            </FormRow>
          ) : (
            <>
              <FormRow label="Viza valabilă de la" id="f-viza1">
                <Input id="f-viza1" type="date" value={form.viza_de_la} onChange={(e) => handle("viza_de_la", e.target.value)} data-testid="adev-viza-de-la" />
              </FormRow>
              <FormRow label="Viza valabilă până la" id="f-viza2">
                <Input id="f-viza2" type="date" value={form.viza_pana_la} onChange={(e) => handle("viza_pana_la", e.target.value)} data-testid="adev-viza-pana-la" />
              </FormRow>
            </>
          )}
          <FormRow label="Nume medic" id="f-medic">
            <Input id="f-medic" value={form.medic_nume} onChange={(e) => handle("medic_nume", e.target.value)} placeholder="ex: Dr. Popescu Ion" data-testid="adev-medic" />
          </FormRow>
          <FormRow label="Cod parafă medic" id="f-cod">
            <Input id="f-cod" value={form.medic_cod} onChange={(e) => handle("medic_cod", e.target.value)} placeholder="ex: Cod 204255" data-testid="adev-medic-cod" />
          </FormRow>
          {federatie === "baschet" && (
            <>
              <FormRow label="Funcție responsabil club" id="f-func">
                <Input id="f-func" value={form.functie_responsabil} onChange={(e) => handle("functie_responsabil", e.target.value)} placeholder="ex: Antrenor principal" />
              </FormRow>
              <FormRow label="Nume responsabil club" id="f-resp">
                <Input id="f-resp" value={form.nume_responsabil} onChange={(e) => handle("nume_responsabil", e.target.value)} />
              </FormRow>
            </>
          )}
        </div>

        {/* === ADEVERINȚĂ TIPĂRIBILĂ === */}
        <div className="bg-white p-8 print:p-12 mt-4 print:mt-0 border-2 border-slate-200 print:border-0 rounded-lg adeverinta-printable" data-testid="adeverinta-printable">
          {federatie === "baschet" ? (
            <AdeverintaBaschet form={form} cnpDigits={cnpDigits} />
          ) : (
            <AdeverintaVolei form={form} cnpDigits={cnpDigits} />
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-3 border-t print:hidden">
          <Button type="button" variant="outline" onClick={() => setOpen(false)} className="gap-2">
            <X className="w-4 h-4" />
            Închide
          </Button>
          <Button type="button" onClick={handlePrint} className="gap-2" data-testid="adeverinta-print">
            <Printer className="w-4 h-4" />
            Tipărește
          </Button>
        </div>

        <style>{`
          @media print {
            body * { visibility: hidden; }
            .adeverinta-printable, .adeverinta-printable * { visibility: visible; }
            .adeverinta-printable {
              position: absolute; left: 0; top: 0; width: 100%;
              padding: 1.5cm !important; margin: 0 !important;
              box-shadow: none !important;
            }
            @page { size: A4 portrait; margin: 0; }

            /* === Adeverința Volei & Baschet — forțare încadrare pe o singură pagină A4 === */
            .adeverinta-printable:has(.adeverinta-volei),
            .adeverinta-printable:has(.adeverinta-baschet) {
              padding: 0.8cm !important;
            }
            .adeverinta-volei, .adeverinta-baschet {
              font-size: 10.5px !important;
              line-height: 1.28 !important;
              page-break-inside: avoid;
              page-break-after: avoid;
              break-inside: avoid;
              max-height: 27.7cm;
              overflow: hidden;
            }
            .adeverinta-volei .volei-header,
            .adeverinta-baschet .baschet-header { margin-bottom: 0.35rem !important; }
            .adeverinta-baschet .baschet-header .w-16 { width: 3rem !important; height: 3rem !important; }
            .adeverinta-baschet .baschet-header .text-2xl { font-size: 1rem !important; }
            .adeverinta-volei h1, .adeverinta-baschet h1 { font-size: 0.95rem !important; line-height: 1.05 !important; margin: 0 !important; }
            .adeverinta-volei h2, .adeverinta-baschet h2 { font-size: 1.05rem !important; margin-bottom: 0.35rem !important; margin-top: 0.35rem !important; }
            .adeverinta-volei h3, .adeverinta-baschet h3 { font-size: 0.9rem !important; margin-bottom: 0.35rem !important; }
            .adeverinta-volei p, .adeverinta-baschet p { margin: 0.15rem 0 !important; }
            .adeverinta-volei .space-y-3 > * + *,
            .adeverinta-baschet .space-y-3 > * + * { margin-top: 0.25rem !important; }
            .adeverinta-volei .mb-5, .adeverinta-baschet .mb-5 { margin-bottom: 0.4rem !important; }
            .adeverinta-volei .mb-6, .adeverinta-baschet .mb-6 { margin-bottom: 0.4rem !important; }
            .adeverinta-volei .mt-3, .adeverinta-baschet .mt-3 { margin-top: 0.25rem !important; }
            .adeverinta-volei .mt-6, .adeverinta-baschet .mt-6 { margin-top: 0.4rem !important; }
            .adeverinta-volei .mt-8, .adeverinta-baschet .mt-8 { margin-top: 0.6rem !important; }
            .adeverinta-volei .pt-2, .adeverinta-baschet .pt-2 { padding-top: 0.2rem !important; }
            .adeverinta-volei .p-2, .adeverinta-baschet .p-2 { padding: 0.25rem !important; }
            .adeverinta-volei .p-3, .adeverinta-baschet .p-3 { padding: 0.35rem !important; }
            .adeverinta-volei .gap-y-3, .adeverinta-baschet .gap-y-3 { row-gap: 0.3rem !important; }
            .adeverinta-volei .gap-6, .adeverinta-baschet .gap-6 { gap: 0.75rem !important; }
            .adeverinta-volei .gap-4, .adeverinta-baschet .gap-4 { gap: 0.5rem !important; }
            .adeverinta-volei .leading-relaxed, .adeverinta-baschet .leading-relaxed { line-height: 1.32 !important; }
            .adeverinta-baschet .text-xs { font-size: 9.5px !important; line-height: 1.25 !important; }
            .adeverinta-baschet .text-sm { font-size: 10.5px !important; line-height: 1.28 !important; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

// === Template Baschet === //
const AdeverintaBaschet = ({ form, cnpDigits }) => (
  <div className="adeverinta-baschet">
    <div className="flex items-center gap-3 mb-6 baschet-header">
      <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center print:bg-orange-500">
        <span className="text-white font-bold text-2xl">FRB</span>
      </div>
      <div>
        <h1 className="text-xl font-bold leading-tight">Federația</h1>
        <h1 className="text-xl font-bold leading-tight">Română</h1>
        <h1 className="text-xl font-bold leading-tight">de Baschet</h1>
      </div>
    </div>

    <h2 className="text-center text-2xl font-bold mb-6 underline tracking-wide">
      ADEVERINȚĂ MEDICALĂ SPORTIV
    </h2>

    <div className="space-y-3 text-sm">
      <PrintRow label="Nume" value={form.nume} />
      <PrintRow label="Prenume" value={form.prenume} />
      <div className="grid grid-cols-3 gap-4">
        <PrintRow label="Data nașterii" value={formatDateRO(form.data_nasterii)} />
        <PrintRow label="Localitatea" value={form.loc_nasterii} />
        <PrintRow label="Țara" value={form.tara} />
      </div>

      <CNPRow cnpDigits={cnpDigits} />

      <div className="grid grid-cols-2 gap-4 pt-2">
        <PrintRow label="Talia sportivului" value={form.talia ? `${form.talia} cm` : ""} />
        <div></div>
        <PrintRow label="Viza medicală valabilă de la data" value={formatDateRO(form.viza_de_la)} />
        <PrintRow label="Viza medicală valabilă până la data" value={formatDateRO(form.viza_pana_la)} />
      </div>
    </div>

    <p className="mt-6 text-sm leading-relaxed text-justify">
      Prin această adeverință, confirm, bazându-mă pe datele ce mi-au fost aduse la cunoștință și după o
      examinare medicală conform legii, că sportivul menționat este <strong>apt medico-sportiv</strong> și poate participa
      la competițiile organizate de Federația Română de Baschet.
    </p>

    <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
      <div className="border border-slate-300 print:border-black rounded p-3">
        <p className="text-xs leading-snug mb-2">
          Mă angajez să cunosc reglementările anti-doping și sunt de acord ca adeverința medicală să fie
          transmisă către ANAD înainte sau în timpul competiției, conform regulamentelor acesteia.
        </p>
        <div className="mt-8 pt-2 border-t border-slate-400">
          <span className="text-xs">Semnătura sportivului</span>
        </div>
      </div>

      <div className="border border-slate-300 print:border-black rounded p-3">
        <p className="text-xs font-semibold mb-1">Semnătura și parafa medicului<br />medicină sportivă</p>
        <div className="mt-3 text-sm">
          <p>{form.medic_nume}</p>
          <p className="text-xs italic">{form.medic_specialitate}</p>
          {form.medic_cod && <p className="text-xs">{form.medic_cod}</p>}
        </div>
        <div className="mt-6 pt-2 border-t border-slate-400">
          <span className="text-xs">Ștampila unității medicale specializate în medicina sportivă</span>
        </div>
      </div>
    </div>

    <div className="mt-6 border border-slate-300 print:border-black rounded p-3 text-xs leading-relaxed text-justify">
      <p>
        Clubul sportiv declară pe proprie răspundere că a trimis sportivul menționat
        la un medic specializat în medicină sportivă, pentru efectuarea controlului
        medical obligatoriu, și își asumă răspunderea pentru corectitudinea și
        completitudinea datelor înscrise în acest formular, precum și pentru orice alte
        consecințe rezultate din nerespectarea obligației de efectuare a controlului
        medical obligatoriu.
      </p>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <PrintRow label="Funcție responsabil club" value={form.functie_responsabil} />
        <PrintRow label="Nume responsabil club" value={form.nume_responsabil} />
      </div>
      <div className="mt-8 pt-2 border-t border-slate-400">
        <span className="text-xs">Semnătura și ștampila clubului (L.S.)</span>
      </div>
    </div>
  </div>
);

// === Template Volei === //
const AdeverintaVolei = ({ form, cnpDigits }) => (
  <div className="adeverinta-volei">
    <div className="flex items-center gap-3 mb-6 volei-header">
      <div className="w-16 h-16 rounded-full bg-blue-700 flex items-center justify-center print:bg-blue-700 relative">
        <span className="text-white font-bold text-xl">FRV</span>
        <span className="absolute -bottom-1 text-[8px] text-white font-semibold">1931</span>
      </div>
      <div>
        <h1 className="text-xl font-bold leading-tight">FEDERAȚIA ROMÂNĂ</h1>
        <h1 className="text-xl font-bold leading-tight">DE VOLEI</h1>
      </div>
    </div>

    <h2 className="text-center text-2xl font-bold mb-2 tracking-wide">
      ADEVERINȚĂ MEDICALĂ
    </h2>
    <h3 className="text-center text-lg font-semibold mb-4 tracking-wide">SPORTIV</h3>

    <div className="bg-slate-100 print:bg-slate-100 border border-slate-300 print:border-black rounded p-2 text-center text-xs font-semibold mb-5">
      ACEASTĂ ADEVERINȚĂ MEDICALĂ ARE VALABILITATE 6 LUNI, ÎNCEPÂND DE LA DATA EXAMINĂRII.
      <br />
      <span className="italic font-normal">(în absența unor stări patologice ulterioare)</span>
    </div>

    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <PrintRow label="Nume" value={form.nume} />
        <PrintRow label="Prenume" value={form.prenume} />
        <PrintRow label="Data nașterii" value={formatDateRO(form.data_nasterii)} />
        <PrintRow label="Locul nașterii" value={form.loc_nasterii} />
      </div>

      <CNPRow cnpDigits={cnpDigits} />

      <div className="grid grid-cols-3 gap-4 pt-2">
        <PrintRow label="Țara" value={form.tara} />
        <PrintRow label="Data examenului medical" value={formatDateRO(form.data_examen)} />
        <PrintRow label="Înălțimea sportivului" value={form.talia ? `${form.talia} cm` : ""} />
      </div>
    </div>

    <p className="mt-6 text-sm leading-relaxed text-justify">
      PRIN ACEASTĂ ADEVERINȚĂ, CONFIRM, BAZÂNDU-MĂ PE DATELE CE MI-AU FOST ADUSE LA CUNOȘTINȚĂ ȘI
      DUPĂ O EXAMINARE MEDICALĂ CONFORM LEGII, CĂ SPORTIVUL MENȚIONAT ESTE <strong>APT MEDICO-SPORTIV</strong> ȘI
      POATE PARTICIPA LA COMPETIȚIILE ORGANIZATE DE FEDERAȚIA ROMÂNĂ DE VOLEI.
    </p>

    <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
      <div className="border border-slate-300 print:border-black rounded p-3">
        <p className="text-xs leading-snug mb-2 font-semibold">
          MĂ ANGAJEZ SĂ CUNOSC REGLEMENTĂRILE ANTI-DOPING.
        </p>
        <p className="text-xs leading-snug mb-2">
          SUNT DE ACORD CA ADEVERINȚA MEDICALĂ SĂ FIE TRANSMISĂ CĂTRE A.N.A.D., ÎNAINTE SAU ÎN
          TIMPUL COMPETIȚIEI, CONFORM REGULAMENTELOR ACESTEIA.
        </p>
        <div className="mt-8 pt-2 border-t border-slate-400">
          <span className="text-xs font-semibold">Semnătura sportivului</span>
        </div>
      </div>

      <div className="border border-slate-300 print:border-black rounded p-3">
        <p className="text-xs font-semibold mb-1">SEMNĂTURA ȘI PARAFA MEDICULUI</p>
        <div className="mt-3 text-sm">
          <p>{form.medic_nume}</p>
          <p className="text-xs italic">{form.medic_specialitate}</p>
          {form.medic_cod && <p className="text-xs">{form.medic_cod}</p>}
        </div>
        <div className="mt-6 pt-2 border-t border-slate-400">
          <span className="text-xs">Ștampila unității medicale specializate în medicina sportivă</span>
        </div>
      </div>
    </div>
  </div>
);

// === Helper components === //
const CNPRow = ({ cnpDigits }) => (
  <div className="flex items-end gap-3 pt-2">
    <div className="text-sm font-medium whitespace-nowrap leading-tight">CNP:</div>
    <div className="flex gap-1">
      {cnpDigits.map((d, i) => (
        <div key={i} className="w-7 h-9 border border-black flex items-center justify-center font-mono text-sm">
          {d.trim()}
        </div>
      ))}
    </div>
  </div>
);

const FormRow = ({ label, id, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs font-medium">{label}</Label>
    {children}
  </div>
);

const PrintRow = ({ label, value }) => (
  <div className="flex items-end gap-2 text-sm">
    <span className="font-medium whitespace-nowrap">{label}:</span>
    <span className="flex-1 border-b border-slate-700 px-2 pb-0.5 min-h-[1.5em]">
      {value || "\u00A0"}
    </span>
  </div>
);
